import PropTypes from 'prop-types';
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
// hooks
import { Button, MenuItem } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { useResponsive } from 'src/hooks/use-responsive';
// routes
import { paths } from 'src/routes/paths';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFUpload,
} from 'src/components/hook-form';

import { RouterLink } from 'src/routes/components';
import {
  bulkUpload,
  createNewInterview,
  fetchInterviews,
} from 'src/services/interview.service';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { LoadingScreen } from 'src/components/loading-screen';
import { getFileExtension } from 'src/utils/get-file-extension';
import { useAuthContext } from 'src/auth/hooks';
import { setProgressQueue } from 'src/store/progressQueueSlice';
import EmailTemplateInterview from './email-template-interview';
import EmailTemplate from '../email-template/view/email-template';
// ----------------------------------------------------------------------

const validTillOptions = [];
for (let i = 1; i <= 30; i++) {
  validTillOptions.push(`${i} Day${i !== 1 ? 's' : ''}`);
}

export default function InterviewNewEditForm({ currentJob }) {
  // router
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type');
  const mdUp = useResponsive('up', 'md');

  // refs
  const inputFileTypeButtonRef = useRef(null);
  const inputSourceFormatButtonRef = useRef(null);

  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const dispatch = useDispatch();

  // states
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputFileType, setInputFileType] = useState('pdf');
  const [inputSourceFormat, setInputSourceFormat] = useState('');
  const [insideButtonClicked, setInsideButtonClicked] = useState(false);
  const [accept, setAccept] = useState({ 'application/pdf': ['.pdf'] });
  const [emailPage, setEmailPage] = useState(false);

  // const [sendEmail,setSendEmail] = useState(true);
  // const [validFor, setValidFor] = useState();

  // memo
  const defaultSingleInterviewValues = useMemo(
    () => ({
      fullname: currentJob?.fullname || '',
      email: currentJob?.email || '',
      phonenumber: currentJob?.phonenumber || '',
      sendEmail: true,
      coverUrl: currentJob?.coverUrl || null,
      valid_for: validTillOptions[6],
    }),
    [currentJob],
  );

  const defaultMultipleInterviewValues = useMemo(
    () => ({
      sendEmail: true,
      valid_for: validTillOptions[6],
    }),
    [],
  );

  const SingleInterviewSchema = Yup.object().shape({
    fullname:
      type === 'single'
        ? Yup.string().required('Full Name is required')
        : Yup.string(),
    email: Yup.string()
      .email('Please enter a valid email')
      .required('Email is required'),
    phonenumber: Yup.string(),
    sendEmail: Yup.bool(),
    coverUrl:
      type === 'single' ? Yup.mixed().nullable() : Yup.mixed().nullable(),
    valid_for: Yup.string(),
  });

  const MultipleInterviewSchema = Yup.object().shape({
    sendEmail: Yup.bool(),
    valid_for: Yup.string(),
  });

  const SingleInterviewMethods = useForm({
    resolver: yupResolver(SingleInterviewSchema),
    defaultValues: defaultSingleInterviewValues,
  });

  const MultipleInterviewMethods = useForm({
    resolver: yupResolver(MultipleInterviewSchema),
    defaultValues: defaultMultipleInterviewValues,
  });

  const [methods, setMethods] = useState(
    type === 'bulk' ? MultipleInterviewMethods : SingleInterviewMethods,
  );

  // variables
  const { reset, setValue, handleSubmit, watch } = methods;
  const inputFileTypeOptions = [
    {
      label: 'Resume',
      value: 'pdf',
    },
    {
      label: 'Excel',
      value: 'excel',
    },
  ];

  const inputSourceFormatOptions = [
    {
      label: 'Naukri',
      value: 'NAUKRI',
      logo: '/assets/icons/brands/naukri.svg',
    },
    {
      label: 'Apna Jobs',
      value: 'APNA_JOBS',
      logo: '/assets/icons/brands/apna_jobs.svg',
    },
    {
      label: 'Internshala',
      value: 'INTERNSHALA',
      logo: '/assets/icons/brands/internshala.svg',
    },
    {
      label: 'Others',
      value: 'HIREINTEL',
      logo: '/assets/icons/brands/office.svg',
    },
  ];

  // functions
  const onInputSourceFormatChange = value => {
    setInputSourceFormat(value);
  };

  const onInputFileTypeChange = value => {
    setInputFileType(value);
  };

  const uploadSingleResume = async data => {
    try {
      const folderName = uuidv4();
      const resumeUrl = await uploadResumeToS3(data.coverUrl, `${folderName}`);
      const reqBody = {
        name: data.fullname,
        email: data.email,
        phone_no: data.phonenumber,
        should_send_email: `${data.sendEmail}`,
        valid_for: data.valid_for,
      };

      if (data.coverUrl) {
        reqBody.resume_url = resumeUrl;
      }

      try {
        const interviewResponse = await createNewInterview(
          reqBody,
          params.jobId,
        );
        if (interviewResponse.success === true) {
          enqueueSnackbar(currentJob ? 'Update success!' : 'Create success!');
        } else {
          enqueueSnackbar({
            message: 'Interview creation failed in API',
            variant: 'error',
          });
        }
      } catch (err) {
        enqueueSnackbar({
          message: 'Interview creation failed',
          variant: 'error',
        });
      }
    } catch (err) {
      alert('Unable to upload resume');
    }
  };

  const addToLocalStorage = ({ bulkRequestId, jobId, jobName }) => {
    const bulkRequestObj = {
      bulkRequestId,
      jobId,
      jobName,
      status: '',
      expiration: new Date().getTime() + 300000,
    };

    const progressQueue = JSON.parse(
      localStorage.getItem(`${user.user_id}_progressQueue`),
    );

    if (progressQueue) {
      const progressIndex = progressQueue.findIndex(
        item => item.bulkRequestId === bulkRequestId,
      );

      if (progressIndex === -1) {
        progressQueue.unshift(bulkRequestObj);
        localStorage.setItem(
          `${user.user_id}_progressQueue`,
          JSON.stringify(progressQueue),
        );

        dispatch(setProgressQueue(progressQueue));
      }
    } else {
      localStorage.setItem(
        `${user.user_id}_progressQueue`,
        JSON.stringify([bulkRequestObj]),
      );
      dispatch(setProgressQueue([bulkRequestObj]));
    }
  };

  const uploadMultipleResume = async data => {
    try {
      const folderName = uuidv4();

      if (files.length === 1) {
        const file = files[0];
        await uploadResumeToS3(
          file,
          folderName,
          file.type,
          getFileExtension(file.name),
        );
      } else {
        const uploadPromises = files.map(async file =>
          uploadResumeToS3(
            file,
            folderName,
            file.type,
            getFileExtension(file.name),
          ),
        );

        await Promise.all(uploadPromises);
      }

      const reqBody = {
        s3_folder_name: `resumes/${folderName}`,
        should_send_email: `${data.sendEmail}`,
        input_format: inputSourceFormat || 'RESUME',
        bulk_request_id: folderName,
        valid_for: data.valid_for,
      };

      const res = await fetchInterviews(params.jobId);
      const jobName = res.body.job_opening.name;

      addToLocalStorage({
        bulkRequestId: folderName,
        jobId: params.jobId,
        jobName,
      });

      await bulkUpload(params.jobId, reqBody);

      enqueueSnackbar({
        message: `Generating interviews usually takes 30 seconds. Hit the browser's refresh for results.`,
        autoHideDuration: 10000,
        variant: 'success',
      });
    } catch (err) {
      alert('Unable to upload resumes');
      enqueueSnackbar({
        message: 'Something went wrong',
        variant: 'error',
      });
    }
  };

  const onSubmit = handleSubmit(async (data, event) => {
    const payload = data;
    payload.valid_for = data.valid_for[0] - '0';
    console.log('payload', payload);
    try {
      setIsSubmitting(true);

      // const day = new Date();
      // const dayPlusSeven = new Date(day);
      // dayPlusSeven.setDate(day.getDate() + 7);
      // const reqDate = dayPlusSeven.toString().slice(4, 24);

      if (type === 'single') {
        await uploadSingleResume(payload);
      } else {
        await uploadMultipleResume(payload);
      }
      setIsSubmitting(false);
      navigate(
        `${paths.dashboard.jobs.details(params.jobId)}?${new URLSearchParams({
          sortBy: 'createdAt',
        }).toString()}`,
      );
    } catch (error) {
      console.error(error);
    }
  });

  const handleSingleDrop = useCallback(
    acceptedFiles => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('coverUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleMultipleDrop = useCallback(
    acceptedFiles => {
      const newFiles = acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      if (inputFileType === 'pdf') {
        setFiles([...files, ...newFiles]);
      } else {
        setFiles([...newFiles]);
      }
    },
    [files, inputFileType],
  );

  const handleDeleteFile = () => {
    setValue('coverUrl', null);
  };

  const handleRemoveFile = inputFile => {
    const filtered = files.filter(file => file !== inputFile);
    setFiles(filtered);
  };

  const handleRemoveAllFiles = () => {
    setFiles([]);
  };

  const manualValidate = () => {
    if (type === 'bulk') {
      if (files.length === 0) {
        enqueueSnackbar({
          message: 'Please upload resume files...',
          variant: 'error',
        });
      } else {
        setEmailPage(true);
      }
    } else {
      const data = methods.getValues();
      const schema =
        type === 'bulk' ? MultipleInterviewSchema : SingleInterviewSchema;
      schema
        .validate(data, { abortEarly: false })
        .then(() => {
          setEmailPage(true);
        })
        .catch(errors => {
          enqueueSnackbar({
            message: 'Please enter valid values for Full Name and Email...',
            variant: 'error',
          });
        });
    }
  };

  // effects
  useEffect(() => {
    if (type === 'single') setMethods(SingleInterviewMethods);
    if (type === 'bulk') setMethods(MultipleInterviewMethods);
  }, [MultipleInterviewMethods, SingleInterviewMethods, type]);

  useEffect(() => {
    if (currentJob) {
      reset(defaultSingleInterviewValues);
    }
  }, [currentJob, defaultSingleInterviewValues, reset]);

  useEffect(() => {
    if (type === 'bulk') {
      if (inputFileType === 'pdf') {
        setAccept({
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        });
      } else if (inputFileType === 'excel') {
        setAccept({
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
          ],
          'text/csv': ['.csv'],
        });
      }
    } else {
      setAccept({
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx'],
      });
    }
  }, [type, inputFileType]);

  useEffect(() => {
    if (params.jobId && !type) {
      navigate(
        `${paths.dashboard.jobs.root}/${
          params.jobId
        }/interview/new?${new URLSearchParams({
          type: 'single',
        }).toString()}`,
      );
    }
  }, [navigate, params.jobId, type]);

  const renderProperties = (
    <Grid sx={{ width: '80%' }} xs={12}>
      <Stack
        spacing={1.5}
        direction="row"
        alignItems="center"
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ mb: '1rem' }}>
            <Typography variant="h5">
              {type === 'single' ? 'Add Candidate' : 'Bulk Upload'}
            </Typography>
          </Box>
          <Box>
            <Button
              component={RouterLink}
              href={`${paths.dashboard.jobs.root}/${params.jobId}`}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Stack>
      <Card>
        <Stack spacing={3} sx={{ p: 3 }}>
          <Stack>
            <RHFUpload
              name="coverUrl"
              maxSize={5000000}
              multiple={type === 'bulk'}
              files={type === 'bulk' ? files : undefined}
              onDelete={handleDeleteFile}
              onRemove={type === 'bulk' ? handleRemoveFile : undefined}
              inputFileTypeButtonRef={inputFileTypeButtonRef}
              inputSourceFormatButtonRef={inputSourceFormatButtonRef}
              inputFileType={inputFileType}
              inputSourceFormat={inputSourceFormat}
              inputFileTypeOptions={inputFileTypeOptions}
              inputSourceFormatOptions={inputSourceFormatOptions}
              onInputFileTypeChange={onInputFileTypeChange}
              onInputSourceFormatChange={onInputSourceFormatChange}
              setInsideButtonClicked={setInsideButtonClicked}
              noClick={type === 'bulk'}
              accept={accept}
              onDrop={type === 'bulk' ? handleMultipleDrop : handleSingleDrop}
            />
          </Stack>
          {!!files.length && (
            <Button
              style={{ alignSelf: 'end' }}
              variant="outlined"
              color="inherit"
              onClick={handleRemoveAllFiles}
            >
              Remove all
            </Button>
          )}
          {type === 'single' && (
            <>
              <Stack spacing={1.5}>
                <RHFTextField name="fullname" placeholder="Full Name" />
              </Stack>
              <Stack spacing={1.5}>
                <RHFTextField name="email" type="email" placeholder="Email" />
              </Stack>
              <Stack spacing={1.5}>
                <RHFTextField name="phonenumber" placeholder="Phone Number" />
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </Grid>
  );

  const getCta = () => {
    if (type === 'bulk') {
      if (inputFileType === 'excel') return 'Generate Interviews';
      return `Generate ${files.length === 0 ? '' : files.length} Interviews`;
    }
    return 'Generate Interview';
  };

  const renderActions = (
    <>
      {mdUp && <Grid md={4} />}
      <Grid
        xs={12}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Stack direction="column" alignItems="center" spacing={1.5}>
          {/* <RHFCheckbox
            name="sendEmail"
            defaultChecked
            label={
              type === 'bulk' ? (
                <Typography variant="body2">
                  Send email to candidates (
                  <Box
                    component="span"
                    ref={emailPreviewButtonRef}
                    onClick={event => {
                      event.preventDefault();
                      setShowEmailPreview(true);
                    }}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                    }}
                  >
                    Preview
                  </Box>
                  )
                </Typography>
              ) : (
                <Typography variant="body2">
                  Send email to candidate (
                  <Box
                    component="span"
                    ref={emailPreviewButtonRef}
                    onClick={event => {
                      event.preventDefault();
                      setShowEmailPreview(true);
                    }}
                    sx={{
                      mx: 0.5,
                      color: 'primary.main',
                      textDecoration: 'underline',
                    }}
                  >
                    Preview
                  </Box>
                  )
                </Typography>
              )
            }
          /> */}
          <Button
            onClick={() => manualValidate()}
            sx={{ width: '8rem' }}
            size="large"
            variant="contained"
          >
            Continue
          </Button>
        </Stack>
      </Grid>
    </>
  );

  watch('sendEmail');
  watch('valid_for');

  const renderTemplate = (
    <EmailTemplate
      heading="Interview Setup"
      onBackClick={setEmailPage}
      type={type}
      methods={methods}
      isSubmitting={isSubmitting}
      validTillOptions={validTillOptions}
      items={files}
      inputFileType={inputFileType}
    />
  );

  return (
    <>
      {isSubmitting && (
        <Stack
          sx={{ height: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <LoadingScreen loadingText="Adding candidates in the workflow" />
        </Stack>
      )}
      {!isSubmitting && (
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Grid
            sx={{ display: 'flex', justifyContent: 'center' }}
            container
            spacing={3}
          >
            {!emailPage && renderProperties}
            {!emailPage && renderActions}
            {emailPage && renderTemplate}
          </Grid>
        </FormProvider>
      )}
    </>
  );
}

InterviewNewEditForm.propTypes = {
  currentJob: PropTypes.object,
};
